// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Layout as AntdLayout,
  AutoComplete,
  Avatar,
  Badge,
  Button,
  Divider,
  Dropdown,
  Grid,
  Input,
  Space,
  Typography,
  theme,
} from "antd";
import type { MenuProps } from "antd";

import { useGetIdentity, useGo, useList, useLogout } from "@refinedev/core";
import React, { useEffect, useState } from "react";

import { IOptions, IShipco, IVessel } from "interfaces";
import debounce from "lodash/debounce";
import { useTheme } from "contexts/ThemeContext";
import { colors } from "config/theme";
import { RoleEnum } from "types/roles";

const {
  SearchOutlined,
  UserOutlined,
  SmileOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  CompassOutlined,
  GlobalOutlined,
  RocketOutlined,
  StarOutlined,
  MailOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  BellOutlined,
} = Icons;
const { Text } = Typography;

const AVATAR_ICONS = [
  UserOutlined,
  SmileOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  CompassOutlined,
  GlobalOutlined,
  RocketOutlined,
  StarOutlined,
];

const ROLE_DISPLAY_MAP: Record<string, string> = {
  DistributorUser: "Distributor User",
  DistributorAdmin: "Distributor Admin",
  DistributorSuperAdmin: "Distributor Super Admin",
  ShipcoUser: "Shipco User",
  ShipcoGroupUser: "Shipco Group User",
  VesselUser: "Vessel User",
};

const APP_VERSION = process.env.REACT_APP_VERSION || "1.0.0";
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "sales@emariner.net";
const FLEET_TRACKING_URL = process.env.REACT_APP_FLEET_TRACKING_URL || "https://app.seallecdis.com";

export const Header: React.FC = () => {
  const { data: user } = useGetIdentity<{
    id: number;
    name: string | null;
    email: string | null;
    avatarIconIndex: number;
    role: string | null;
  }>();
  const { mutate: logout } = useLogout();
  const [value, setValue] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [options, setOptions] = useState<IOptions[]>([]);
  const go = useGo();
  useTheme();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // < 768px

  const shouldRenderHeader = user && user.name;
  const AvatarIcon = user ? AVATAR_ICONS[user.avatarIconIndex] || UserOutlined : UserOutlined;

  const dropdownItems: MenuProps['items'] = user ? [
    {
      key: 'name',
      label: <Text strong>{user.name}</Text>,
      disabled: true,
    },
    {
      key: 'email',
      label: <Text type="secondary">{user.email}</Text>,
      icon: <MailOutlined />,
      disabled: true,
    },
    {
      key: 'role',
      label: <Text type="secondary">{ROLE_DISPLAY_MAP[user.role || ''] || user.role}</Text>,
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'support',
      label: 'Support',
      icon: <QuestionCircleOutlined />,
      onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`, '_blank'),
    },
    {
      key: 'version',
      label: <Text type="secondary">Version {APP_VERSION}</Text>,
      icon: <InfoCircleOutlined />,
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: () => logout(),
    },
  ] : [];

  const renderTitle = (title: string) => {
    return (
      <Text strong style={{ fontSize: "16px" }}>
        {title}
      </Text>
    );
  };

  const renderItem = (title: string, resource: string, id: string) => {
    return {
      value: `${resource}:${id}`,
      label: <Text>{title}</Text>,
    };
  };

  const {
    result: vesselByNameResult,
    query: { refetch: refetchVesselByName, isFetched: isFetchedByName }
  } = useList<IVessel>({
    resource: "vessel",
    queryOptions: { enabled: false },
    filters: [{ field: "name", operator: "contains", value }],
    pagination: { pageSize: 100 }
  });

  const {
    result: vesselByIMOResult,
    query: { refetch: refetchVesselByIMO, isFetched: isFetchedByIMO }
  } = useList<IVessel>({
    resource: "vessel",
    queryOptions: { enabled: false },
    filters: [{ field: "imoNumber", operator: "contains", value }],
    pagination: { pageSize: 100 }
  });

  const {
    result: shipcoResult,
    query: { refetch: refetchShipcos, isFetched: isFetchedShipcos }
  } = useList<IShipco>({
    resource: "shipco",
    queryOptions: { enabled: false },
    filters: [{ field: "registrationName", operator: "contains", value }]
  });

  // Trigger refetch when search value changes
  useEffect(() => {
    if (!value) {
      setOptions([]);
      return;
    }
    refetchVesselByName();
    refetchVesselByIMO();
    refetchShipcos();
  }, [refetchShipcos, refetchVesselByName, refetchVesselByIMO, value]);

  // Combine and deduplicate results when all queries complete
  useEffect(() => {
    if (!value || !isFetchedByName || !isFetchedByIMO || !isFetchedShipcos) {
      return;
    }

    const newOptions: IOptions[] = [];

    // Combine vessel results and deduplicate by ID
    const vesselMap = new Map<string, IVessel>();
    vesselByNameResult?.data?.forEach((item) => vesselMap.set(item.id, item));
    vesselByIMOResult?.data?.forEach((item) => vesselMap.set(item.id, item));

    const uniqueVessels = Array.from(vesselMap.values());
    if (uniqueVessels.length > 0) {
      const vesselOptionGroup = uniqueVessels.map((item) =>
        renderItem(`${item.imoNumber} - ${item.name}`, "vessel", item.id)
      );
      newOptions.push({ label: renderTitle("Vessels"), options: vesselOptionGroup });
    }

    // Add shipco results
    if (shipcoResult?.data && shipcoResult.data.length > 0) {
      const shipcoOptionGroup = shipcoResult.data.map((item) =>
        renderItem(item.registrationName, "shipco", item.id)
      );
      newOptions.push({ label: renderTitle("Shipping Companies"), options: shipcoOptionGroup });
    }

    setOptions(newOptions);
  }, [value, vesselByNameResult, vesselByIMOResult, shipcoResult, isFetchedByName, isFetchedByIMO, isFetchedShipcos]);

  const canSearch =
    user?.role === RoleEnum.DistributorAdmin ||
    user?.role === RoleEnum.DistributorSuperAdmin ||
    user?.role === RoleEnum.DistributorUser ||
    user?.role === RoleEnum.ShipcoUser;

  return shouldRenderHeader ? (
    <AntdLayout.Header
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        padding: isMobile ? "12px 16px" : "0px 24px",
        minHeight: 64,
        height: "auto",
        backgroundColor: token.colorBgContainer,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {/* Left - Search */}
      {canSearch ? (
        <AutoComplete
          style={{
            flex: isMobile ? "1 1 100%" : "0 1 500px",
            minWidth: isMobile ? "100%" : 280,
            order: isMobile ? 1 : 0,
          }}
          options={options}
          filterOption={false}
          value={searchText}
          onSearch={debounce((text: string) => {
            setSearchText(text);
            setValue(text);
          }, 500)}
          onChange={(text: string) => setSearchText(text)}
          onSelect={(val: string) => {
            const [resource, id] = val.split(":");
            setSearchText("");
            setValue("");
            go({ to: `/${resource}/show/${id}`, type: "push" });
          }}
        >
          <Input
            size="large"
            placeholder="Search Vessel, IMO or Shipco"
            suffix={<SearchOutlined />}
            style={{
              background: token.colorFillQuaternary,
              borderRadius: 8,
            }}
          />
        </AutoComplete>
      ) : (
        <div />
      )}

      {/* Right section */}
      <div style={{ order: 0 }}>
        <Space size="small">
          {/* External links - visible on all screen sizes */}
          {(user.role === RoleEnum.DistributorAdmin ||
            user.role === RoleEnum.DistributorSuperAdmin ||
            user.role === RoleEnum.DistributorUser ||
            user.role === RoleEnum.ShipcoUser ||
            user.role === RoleEnum.VesselUser) && (
              <Space
                size={4}
                style={{
                  padding: "2px 8px",
                  background: token.colorFillQuaternary,
                  borderRadius: 6,
                  height: 32,
                  alignItems: "center",
                }}
              >
                {user.role !== RoleEnum.VesselUser && (
                  <Button
                    type="text"
                    size="small"
                    href={FLEET_TRACKING_URL}
                    target="_blank"
                    style={{ color: token.colorTextSecondary }}
                  >
                    Fleet Tracking {!isMobile && <ExportOutlined style={{ fontSize: 10, marginLeft: 4 }} />}
                  </Button>
                )}
                {user.role !== RoleEnum.VesselUser && (
                  <Divider type="vertical" style={{ margin: "0 4px" }} />
                )}
                <Button
                  type="text"
                  size="small"
                  href={process.env.REACT_APP_WITHERBY_URL || "https://www.witherbyconnect.com/"}
                  target="_blank"
                  style={{ color: token.colorTextSecondary }}
                >
                  Witherby {!isMobile && <ExportOutlined style={{ fontSize: 10, marginLeft: 4 }} />}
                </Button>
              </Space>
            )}
          <Divider type="vertical" />
          <Badge count={0} size="small">
            <Button
              type="text"
              icon={<BellOutlined style={{ fontSize: 18 }} />}
              style={{ color: token.colorTextSecondary }}
            />
          </Badge>
          <Dropdown menu={{ items: dropdownItems }} trigger={['click']} placement="bottomRight">
            <Avatar
              style={{ backgroundColor: colors.primary, cursor: 'pointer' }}
              size="large"
              icon={<AvatarIcon />}
              alt={user?.name ?? undefined}
            />
          </Dropdown>
        </Space>
      </div>
    </AntdLayout.Header>
  ) : null;
};
